import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '@/trpc/client';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { JobCard } from '@/components/JobCard';
import { Package, MapPin, CheckCheck, DollarSign, Loader2 } from 'lucide-react';
import type { Job, Booking, User } from '@prisma/client';
import { JobStatus, BookingStatus } from '@logistics-platform/types';
import { toast } from 'sonner';
import AnalyticsDashboard from '@/components/analytics/AnalyticsDashboard';

type JobWithBooking = Job & {
  bookings: (Booking & { transporter: User & { profile?: { fullName: string | null } | null }, assignments: any[] })[];
  priceAmount: number | any; // Handle Decimal vs Number conflict
};

const JobCardSkeleton: React.FC = () => (
  <div className="flex flex-col space-y-3 p-4 border rounded-lg bg-card/50">
    <div className="flex items-center">
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-5 w-1/4 ml-auto" />
    </div>
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
    </div>
    <div className="flex justify-end pt-2">
      <Skeleton className="h-8 w-1/3" />
    </div>
  </div>
);

const ClientDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const utils = trpc.useUtils();
  const { data: myJobs, isLoading, isError } = trpc.job.getMyJobs.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 30000,
  });

  const confirmDeliveryMutation = trpc.booking.confirmDelivery.useMutation({
    onSuccess: () => {
      toast.success('Delivery confirmed! Job completed.');
      utils.job.getMyJobs.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const [tab, setTab] = useState<'all' | 'active' | 'inProgress' | 'completed' | 'cancelled' | 'analytics'>('all');

  const allJobs = myJobs ?? [];
  const activeJobs = allJobs.filter(j => j.status === JobStatus.ACTIVE);
  const inProgressJobs = allJobs.filter(j => j.status === JobStatus.IN_PROGRESS);
  const completedJobs = allJobs.filter(j => j.status === JobStatus.COMPLETED);
  const cancelledJobs = allJobs.filter(j => j.status === JobStatus.CANCELLED);

  const totalSpentMWK = completedJobs.reduce((sum, job) => {
    // Calculate spent based on job price and load count (simple estimation if not tracking actual payments here)
    // Or use analytics data if available, but for now derive from jobs
    return sum + (Number(job.priceAmount) * job.loadCount);
  }, 0);

  const renderJobs = () => {
    if (isLoading) {
      return Array.from({ length: 3 }).map((_, i) => <JobCardSkeleton key={i} />);
    }
    if (isError) {
      return <p className="text-destructive">Could not load jobs.</p>;
    }

    let list: JobWithBooking[] = [];
    switch (tab) {
      case 'all': list = allJobs; break;
      case 'active': list = activeJobs; break;
      case 'inProgress': list = inProgressJobs; break;
      case 'completed': list = completedJobs; break;
      case 'cancelled': list = cancelledJobs; break;
      default: list = allJobs;
    }

    if (list.length === 0) {
      return (
        <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
          <h3 className="text-lg font-semibold">
            No {tab === 'all' ? '' : tab.replace(/([A-Z])/g, ' $1').toLowerCase()} jobs found.
          </h3>
          {tab === 'all' && (
            <Button asChild className="mt-4" variant="premium">
              <Link to="/jobs/create">Post Your First Job</Link>
            </Button>
          )}
        </div>
      );
    }

    return list.map((job: JobWithBooking) => {
      const activeBooking = job.bookings.find(
        (b) => b.status !== BookingStatus.REJECTED && b.status !== BookingStatus.CANCELLED
      );
      const isDelivered = activeBooking?.status === BookingStatus.DELIVERED;
      const hasTrackableBooking = job.bookings.some(b =>
        [BookingStatus.IN_TRANSIT, BookingStatus.PICKED_UP, BookingStatus.AT_DELIVERY].includes(b.status as any)
      );

      return (
        <div key={job.id} className="border-b pb-4 mb-4">
          <JobCard job={job} />
          <div className="mt-2 flex justify-end gap-2">
            {(hasTrackableBooking || job.status === JobStatus.IN_PROGRESS) && (
              <Button size="sm" variant="default" asChild className="gap-1">
                <Link to={`/dashboard/jobs/${job.id}/track`}>
                  <MapPin className="w-4 h-4" />
                  Live Track
                </Link>
              </Button>
            )}
            {!isDelivered ? (
              <Button size="sm" variant="outline" asChild>
                <Link to={`/jobs/${job.id}`}>View Details</Link>
              </Button>
            ) : (
              job.status !== JobStatus.COMPLETED && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => confirmDeliveryMutation.mutate({ bookingId: activeBooking?.id || '' })}
                  disabled={confirmDeliveryMutation.isLoading || !activeBooking}
                  className="gap-1"
                >
                  {confirmDeliveryMutation.isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}
                  {confirmDeliveryMutation.isLoading ? 'Confirming...' : 'Confirm Delivery'}
                </Button>
              )
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col h-full bg-surface min-h-screen">
      <div className="container mx-auto px-2 py-4 sm:px-4 sm:py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-8 gap-3 sm:gap-4">
          <h1 className="text-xl sm:text-3xl font-bold">Client Dashboard</h1>
          <Button asChild variant="premium" size="sm" className="w-full sm:w-auto">
            <Link to="/jobs/create">New Job</Link>
          </Button>
        </div>

        <div className="flex overflow-x-auto space-x-4 border-b mb-4 sm:mb-6 pb-1 no-scrollbar">
          {['all', 'active', 'inProgress', 'completed', 'cancelled', 'analytics'].map((t) => (
            <button
              key={t}
              className={`pb-2 text-sm font-medium whitespace-nowrap capitalize ${tab === t ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
              onClick={() => setTab(t as any)}
            >
              {t === 'inProgress' ? 'In Progress' : t}
              {t !== 'analytics' && ` (${t === 'all' ? allJobs.length :
                t === 'active' ? activeJobs.length :
                  t === 'inProgress' ? inProgressJobs.length :
                    t === 'completed' ? completedJobs.length :
                      cancelledJobs.length
                })`}
            </button>
          ))}
        </div>

        {tab === 'analytics' ? (
          <AnalyticsDashboard />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
              <Card className="p-3 sm:p-6">
                <CardHeader className="p-0 pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-xs sm:text-sm font-medium">All Jobs</CardTitle>
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-0">
                  <div className="text-lg sm:text-2xl font-bold">{allJobs.length}</div>
                </CardContent>
              </Card>

              <Card className="p-3 sm:p-6">
                <CardHeader className="p-0 pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-xs sm:text-sm font-medium">Active & In Progress</CardTitle>
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-0">
                  <div className="text-lg sm:text-2xl font-bold">{activeJobs.length + inProgressJobs.length}</div>
                </CardContent>
              </Card>

              <Card className="p-3 sm:p-6">
                <CardHeader className="p-0 pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-xs sm:text-sm font-medium">Completed</CardTitle>
                  <CheckCheck className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-0">
                  <div className="text-lg sm:text-2xl font-bold">{completedJobs.length}</div>
                </CardContent>
              </Card>

              <Card className="p-3 sm:p-6">
                <CardHeader className="p-0 pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-xs sm:text-sm font-medium">Total Value</CardTitle>
                  <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-0">
                  <div className="text-lg sm:text-2xl font-bold">MWK {totalSpentMWK.toLocaleString()}</div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">{renderJobs()}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
